import { DateTime } from 'luxon';
import { isBundle, SelectedItems } from 'types/SelectedItem';
import {
  Ticket,
  TicketAddon,
  TicketTypeBundle,
  TicketTypeBundleItem,
} from 'types/TicketsState';
import { dateFormatForCalendar } from './calendar';
import {
  getFeePrice,
  getTaxPrice,
  getTicketPriceByQuantity,
  hasUnbundledAddons as ticketHasUnbundledAddons,
} from './tickets';
import {
  EventFeesData,
  TicketSalePageEvent,
} from 'app/pages/TicketSalePage/slice/types';

export const getTicketTypes = (
  items: TicketTypeBundleItem[],
  ticketTypes: Ticket[],
) => {
  return items.map(item => {
    const ticketType = ticketTypes.find(
      ticketType => ticketType.id === item.idTicketType,
    );
    if (!ticketType) {
      throw new Error('Bundle item has no associated ticket type');
    }
    return ticketType;
  });
};

// returns all unique addons for a bundle we filter out addons that have the same name
// ex: if a bundle contains one ticket with addon A (uses = 1) and another ticket with addon A (uses = 1)
// we have to return addon A once with uses = 2
export const getAddons = (ticketTypes: Ticket[], bundled: boolean) => {
  let result: TicketAddon[] = [];
  ticketTypes.forEach(ticketType => {
    const bundledAddons = ticketType.ticketaddons.filter(
      addon => !!addon.is_bundled === bundled,
    );

    bundledAddons.forEach(addon => {
      const existingAddonIndex = result.findIndex(
        resultAddon => resultAddon.name === addon.name,
      );
      if (existingAddonIndex === -1) {
        result.push(addon);
      } else if (addon.uses > 0) {
        result[existingAddonIndex] = {
          ...result[existingAddonIndex],
          uses: result[existingAddonIndex].uses + addon.uses,
        };
      }
    });
  });
  return result;
};

export const hasUnbundledAddons = (ticketTypes: Ticket[]) =>
  ticketTypes.some(ticketHasUnbundledAddons);

export const getSalePeriod = (ticketTypes: Ticket[]) => ({
  sold_from: getMaximumSoldFrom(ticketTypes),
  sold_to: getMinimumSoldTo(ticketTypes),
});

const getMaximumSoldFrom = (ticketTypes: Ticket[]) => {
  const sortedTicketTypes = ticketTypes.filter(
    ticketType => ticketType.sold_from !== null,
  );

  sortedTicketTypes.sort(
    (a, b) =>
      new Date(b.sold_from!).getTime() - new Date(a.sold_from!).getTime(),
  );

  return sortedTicketTypes[0]?.sold_from ?? null;
};

const getMinimumSoldTo = (ticketTypes: Ticket[]) => {
  const sortedTicketTypes = ticketTypes
    .filter(ticketType => ticketType.sold_to !== null)
    .sort(
      (a, b) => new Date(a.sold_to!).getTime() - new Date(b.sold_to!).getTime(),
    );

  return sortedTicketTypes[0]?.sold_to ?? null;
};

export const bundleHasCalendar = (ticketTypes: Ticket[]) =>
  ticketTypes.some(ticket => ticket.hasCalendar);

export const getSelectedTicketQuantityFromAllBundles = (
  bundles: TicketTypeBundle[],
  selectedItems: SelectedItems,
  ticketId: string,
  date?: DateTime,
) => {
  const allBundlesForThisTicket = bundles.filter(bundle =>
    bundle.items.some(item => item.idTicketType === ticketId),
  );

  if (allBundlesForThisTicket.length === 0) return 0;

  let selectedQuantity = 0;

  for (const bundle of allBundlesForThisTicket) {
    const selectedBundle = selectedItems.find(
      item =>
        isBundle(item) &&
        item.id === bundle.id &&
        (date ? item.date === date.toFormat(dateFormatForCalendar) : true),
    );

    if (!selectedBundle) continue;

    const bundleItem = bundle.items.find(
      item => item.idTicketType === ticketId,
    ) as TicketTypeBundleItem;

    selectedQuantity += selectedBundle.quantity * bundleItem.quantity;
  }

  return selectedQuantity;
};

const getTicketTypesTotalPrice = (
  ticketTypes: Ticket[],
  items: TicketTypeBundleItem[],
) => {
  let totalPrice = 0;

  ticketTypes.forEach(ticket => {
    const item = items.find(item => item.idTicketType === ticket.id)!;
    totalPrice +=
      getTicketPriceByQuantity(ticket.ticketprice, item.quantity).price *
      item.quantity;
  });

  return totalPrice;
};

const getTotalNumberOfItems = (items: TicketTypeBundleItem[]) =>
  items.reduce((acc, item) => acc + item.quantity, 0);

const getBundleDiscountPerTicket = (
  bundlePrice: number,
  ticketTypes: Ticket[],
  items: TicketTypeBundleItem[],
) => {
  const totalPrice = getTicketTypesTotalPrice(ticketTypes, items);
  const totalNumberOfItems = getTotalNumberOfItems(items);

  return (totalPrice - bundlePrice) / totalNumberOfItems;
};

export const getBundleFeePrice = (
  fees: EventFeesData,
  ticketTypes: Ticket[],
  bundleItems: TicketTypeBundleItem[],
  bundlePrice: number,
) => {
  let totalFee = 0;

  bundleItems.forEach(item => {
    const ticket = ticketTypes.find(ticket => ticket.id === item.idTicketType)!;

    const ticketPrice =
      getTicketPriceByQuantity(ticket.ticketprice, item.quantity).price -
      getBundleDiscountPerTicket(bundlePrice, ticketTypes, bundleItems);

    const fee = getFeePrice(ticketPrice, fees) * item.quantity;

    totalFee += fee;
  });

  return totalFee;
};

export const getBundleTotalPrice = (
  event: TicketSalePageEvent,
  bundle: TicketTypeBundle,
  ticketTypes: Ticket[],
) => {
  let total = bundle.price;

  // tax
  total += getTaxPrice(bundle.price, event.tax_rate.rate);

  // fee
  if (bundle.absorbFees === false) {
    const fee = getBundleFeePrice(
      event.fees,
      ticketTypes,
      bundle.items,
      bundle.price,
    );
    total += fee;
    total += getTaxPrice(fee, event.fee_tax_rate.rate);
  }

  return total;
};
